import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Heart, Sparkles, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useClickTracking } from "@/hooks/useClickTracking";
import { useMultilingualContent } from "@/hooks/useMultilingualContent";
import { useTranslation } from "react-i18next";

export const AboutSection = () => {
  const { i18n } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const { trackClick } = useClickTracking();

  const [content, setContent] = useState({
    about_title_main: "",
    about_title_accent: "",
    about_paragraph_1: "",
    about_paragraph_2: "",
    about_card_1_title: "",
    about_card_1_desc: "",
    about_card_2_title: "",
    about_card_2_desc: "",
    about_card_3_title: "",
    about_card_3_desc: "",
    about_card_4_title: "",
    about_card_4_desc: "",
  });

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from("site_content")
        .select("content_key, content_value, content_value_en, content_value_es")
        .in("content_key", [
          "about_title_main",
          "about_title_accent",
          "about_paragraph_1",
          "about_paragraph_2",
          "about_card_1_title",
          "about_card_1_desc",
          "about_card_2_title",
          "about_card_2_desc",
          "about_card_3_title",
          "about_card_3_desc",
          "about_card_4_title",
          "about_card_4_desc",
        ]);

      if (data) {
        const transformed = transformContent(data);
        setContent({
          about_title_main: transformed.about_title_main || "",
          about_title_accent: transformed.about_title_accent || "",
          about_paragraph_1: transformed.about_paragraph_1 || "",
          about_paragraph_2: transformed.about_paragraph_2 || "",
          about_card_1_title: transformed.about_card_1_title || "",
          about_card_1_desc: transformed.about_card_1_desc || "",
          about_card_2_title: transformed.about_card_2_title || "",
          about_card_2_desc: transformed.about_card_2_desc || "",
          about_card_3_title: transformed.about_card_3_title || "",
          about_card_3_desc: transformed.about_card_3_desc || "",
          about_card_4_title: transformed.about_card_4_title || "",
          about_card_4_desc: transformed.about_card_4_desc || "",
        });
      }
    };

    fetchContent();

    // Subscribe to real-time updates for site_content changes
    const channel = supabase.channel(`about-content-changes-${Math.random().toString(36).slice(2)}`, {
      config: {
        broadcast: { self: true }
      }
    });

    channel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_content',
          filter: 'content_key=in.(about_title_main,about_title_accent,about_paragraph_1,about_paragraph_2,about_card_1_title,about_card_1_desc,about_card_2_title,about_card_2_desc,about_card_3_title,about_card_3_desc,about_card_4_title,about_card_4_desc)'
        },
        (payload) => {
          fetchContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [i18n.language]);

  // Accent word highlight within title
  const highlightAccent = (main: string, accent: string): JSX.Element | string => {
    if (!main) return '';
    if (!accent) return main;
    const lowerMain = main.toLowerCase();
    const lowerAccent = accent.toLowerCase();
    const index = lowerMain.indexOf(lowerAccent);
    if (index === -1) {
      return (
        <>
          {main} <span className="gradient-text">{accent}</span>
        </>
      );
    }
    return (
      <>
        {main.slice(0, index)}
        <span className="gradient-text">{main.slice(index, index + accent.length)}</span>
        {main.slice(index + accent.length)}
      </>
    );
  };

  return (
    <section id="about" className="py-32 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/30 via-background to-accent/10"></div>
      
      <div className="container max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
                {highlightAccent(content.about_title_main, content.about_title_accent)}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {content.about_paragraph_1}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {content.about_paragraph_2}
              </p>
            </div>

          <div className="grid grid-cols-2 gap-4 md:gap-6 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <Card className="p-4 sm:p-6 md:p-8 glass-effect hover-lift text-center group cursor-pointer animate-fade-in" style={{ animationDelay: "0.4s" }} onClick={() => trackClick('about', 'About Card - Autêntica', '')}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full gradient-instagram mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="font-bold mb-1 md:mb-2 text-sm sm:text-base md:text-lg lg:text-xl">{content.about_card_1_title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">{content.about_card_1_desc}</p>
            </Card>

            <Card className="p-4 sm:p-6 md:p-8 glass-effect hover-lift text-center group mt-6 md:mt-8 cursor-pointer animate-fade-in" style={{ animationDelay: "0.5s" }} onClick={() => trackClick('about', 'About Card - Criativa', '')}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full gradient-instagram mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="font-bold mb-1 md:mb-2 text-sm sm:text-base md:text-lg lg:text-xl px-2 md:-translate-x-1 lg:translate-x-0">{content.about_card_2_title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">{content.about_card_2_desc}</p>
            </Card>

            <Card className="p-4 sm:p-6 md:p-8 glass-effect hover-lift text-center group cursor-pointer animate-fade-in" style={{ animationDelay: "0.6s" }} onClick={() => trackClick('about', 'About Card - Global', '')}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full gradient-instagram mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="font-bold mb-1 md:mb-2 text-sm sm:text-base md:text-lg lg:text-xl px-2">{content.about_card_3_title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">{content.about_card_3_desc}</p>
            </Card>

            <Card className="p-4 sm:p-6 md:p-8 glass-effect hover-lift text-center group mt-6 md:mt-8 cursor-pointer animate-fade-in" style={{ animationDelay: "0.7s" }} onClick={() => trackClick('about', 'About Card - Comunidade', '')}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full gradient-instagram mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="currentColor" />
              </div>
              <h3 className="font-bold mb-1 md:mb-2 text-sm sm:text-base md:text-lg lg:text-xl break-words">{content.about_card_4_title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">{content.about_card_4_desc}</p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
